import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import axios from "axios";
import {Status} from "./status";
import {
    Draft,
    DraftComponent, DraftComponentCategory,
    DraftComponents,
    DraftElement, DraftElementItem
} from "../Draft";
import {parseUrl} from "../../routes/common/FilesBrowser";
import {defaultModel} from "../../ai/Prompt";
import {StorageProvider, StorageProviderSettings} from "../../storage/providers/StorageProvider";


export interface DraftState {
    draft: Draft,
    status: Status,
    saved?: Date,
    model: string
    saving: boolean,
    error: string | null | undefined
}

const initialState: DraftState = {
    draft: new Draft(),
    status: Status.Idle,
    saved: undefined,
    model: defaultModel,
    saving: false,
    error: null,
}

export const setModel = createAsyncThunk('setModel', async (params: { model: string }) => {
    return params.model
})

export const resetDraft = createAsyncThunk('resetDraft', async (params: { resetTitle: boolean }) => {
    return params
})

export const fetchDraft = createAsyncThunk('fetchDraft', async (params: {
    storageProvider: StorageProvider | undefined,
    path: string
}) => {
    const response = await axios.get(parseUrl(params.storageProvider, `draft.json`,  params.path))
    return response.data
})

export const saveDraft = createAsyncThunk('saveDraft', async (params: {
    storageProvider: StorageProvider | undefined,
    draft: Draft,
    path: string,
}) => {
    if (params.storageProvider) {
        await params.storageProvider.upload(
            JSON.stringify(params.draft),
            `${params.path}draft.json`,
            () => {
            }
        )
    }
    return new Date(Date.now())
})

export const addGenre = createAsyncThunk('addGenre', async (params: { genre: string, }) => {
    return params.genre
})

export const removeGenre = createAsyncThunk('removeGenre', async (params: { genre: string, }) => {
    return params.genre
})

export const togglePlot = createAsyncThunk('togglePlot', async () => {
    return void 0
})

export const setPerson = createAsyncThunk('setPerson', async (params: { person: number }) => {
    return params.person
})

export const setLines = createAsyncThunk('setLines', async (params: { lines: number }) => {
    return params.lines
})

export const updateTitle = createAsyncThunk('updateTitle', async (params: { title: string }) => {
    return params.title
})

export const updateSynopsis = createAsyncThunk('updateSynopsis', async (params: { text: string }) => {
    return params.text
})

export const updateActs = createAsyncThunk('updateActs', async (params: {
    acts: { id: number, title: string, description: string, chapters: [] }[]
}) => {
    return params.acts
})

export const updateLinesContent = createAsyncThunk('updateLinesContent', async (params: {
    lines: { id: number, title: string, description: string }[]
}) => {
    return params.lines
})

export const updateAct = createAsyncThunk('updateAct', async (params: {
    act: { id: number, title?: string, description?: string, chapters: [] }
}) => {
    return params.act
})

export const updateLine = createAsyncThunk('updateLine', async (params: {
    line: { id: number, title: string, description: string }
}) => {
    return params.line
})

export const updateChapter = createAsyncThunk('updateChapter', async (params: {
    chapter: { id: number, title?: string, description?: string, sections: [] }
}) => {
    return params.chapter
})

export const updateSection = createAsyncThunk('updateSection', async (params: {
    section: { id: number, title?: string, description?: string, scenes: [] }
}) => {
    return params.section
})

export const updateScene = createAsyncThunk('updateScene', async (params: {
    scene: { id: number, title?: string, description?: string, shots: [] }
}) => {
    return params.scene
})

export const updateShot = createAsyncThunk('updateShot', async (params: {
    shot: {
        id: number,
        title: string,
        description: string,
        environment: string,
        place: string,
        time: string,
        narration: { text: string, character?: string, expression?: string },
    }
}) => {
    return params.shot
})

export const addChapter = createAsyncThunk('addChapter', async (params: {
    actId: number,
    chapter: { id: number, title: string, description: string }
}) => {
    return params
})

export const addScene = createAsyncThunk('addScene', async (params: {
    chapterId: number,
    scene: { id: number, title: string, description: string }
}) => {
    return params
})

export const addShot = createAsyncThunk('addShot', async (params: {
    sceneId: number,
    shot: {
        id: number,
        title: string,
        description: string,
        environment: string,
        place: string,
        time: string,
        narration: { text: string, character?: string }
    }
}) => {
    return params
})

export const updateSetting = createAsyncThunk('updateSetting', async (params: {
    setting: DraftComponents,
}) => {
    return params
})


export const updateSettingCategory = createAsyncThunk('updateSettingCategory', async (params: {
    category: DraftComponentCategory
}) => {
    return params
})

export const addSettingCategory = createAsyncThunk('addSettingCategory', async (params: {
    category: DraftComponentCategory,
}) => {
    return params
})

export const deleteSettingCategory = createAsyncThunk('deleteSettingCategory', async (params: {
    categoryKey: string,
}) => {
    return params
})


export const updateComponent = createAsyncThunk('updateComponent', async (params: {
    category: string,
    component: DraftComponent,
}) => {
    return params
})

export const addComponent = createAsyncThunk('addComponents', async (params: {
    category: string,
    component: DraftComponent,
}) => {
    return {
        ...params,
        components: [params.component]
    }
})

export const addComponents = createAsyncThunk('addComponents', async (params: {
    category: string,
    components: DraftComponent[],
}) => {
    return params
})

export const deleteComponent = createAsyncThunk('deleteComponent', async (params: {
    category: string,
    componentId: number,
}) => {
    return params
})

export const updateElement = createAsyncThunk('updateElements', async (params: {
    category: string,
    componentId: number,
    element: DraftElement,
}) => {
    return {...params, elements: [params.element]}
})

export const updateElements = createAsyncThunk('updateElements', async (params: {
    category: string,
    componentId: number,
    elements: DraftElement[],
}) => {
    return params
})

export const addElement = createAsyncThunk('addElements', async (params: {
    category: string,
    componentId: number,
    element: DraftElement,
}) => {
    return {
        ...params,
        elements: [params.element]
    }
})

export const addElements = createAsyncThunk('addElements', async (params: {
    category: string,
    componentId: number,
    elements: DraftElement[],
}) => {
    return params
})

export const deleteElement = createAsyncThunk('deleteElement', async (params: {
    category: string,
    componentId: number,
    elementId: number,
}) => {
    return params
})

export const addLoreItem = createAsyncThunk('addLoreItem', async (params: {
    category: string,
    componentId: number,
    elementId: number,
    item: DraftElementItem
}) => {
    return params
})

export const updateLoreItem = createAsyncThunk('updateLoreItems', async (params: {
    category: string,
    componentId: number,
    elementId: number,
    item: DraftElementItem
}) => {
    return {...params, items: [params.item]}
})

export const updateLoreItems = createAsyncThunk('updateLoreItems', async (params: {
    category: string,
    componentId: number,
    elementId: number,
    items: DraftElementItem[]
}) => {
    return params
})

export const deleteLoreItem = createAsyncThunk('deleteLoreItem', async (params: {
    category: string,
    componentId: number,
    elementId: number,
    itemId: number,
}) => {
    return params
})

export const draftSlice = createSlice({
    name: 'drafts',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(setModel.fulfilled, (state, action) => {
                state.model = action.payload
            })
            .addCase(resetDraft.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    story: {
                        ...initialState.draft.story,
                        title: action.payload.resetTitle ? "Untitled" : state.draft.story.title
                    },
                }
            })
            .addCase(fetchDraft.fulfilled, (state, action) => {
                const draft = action.payload
                if (!draft.setting) {
                    draft.setting = new DraftComponents()
                }
                state.draft = draft
            })
            .addCase(fetchDraft.rejected, (state, action) => {
                state.draft = initialState.draft
            })
            .addCase(saveDraft.pending, (state, action) => {
                state.saving = true
            })
            .addCase(saveDraft.fulfilled, (state, action) => {
                state.saved = action.payload
                state.saving = false
            })
            .addCase(addGenre.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    properties: {
                        ...state.draft.properties,
                        genre: state.draft.properties.genre.concat([action.payload])
                    }
                }
            })
            .addCase(removeGenre.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    properties: {
                        ...state.draft.properties,
                        genre: state.draft.properties.genre.filter(genre => genre !== action.payload)
                    }
                }
            })
            .addCase(togglePlot.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    properties: {...state.draft.properties, linear: !state.draft.properties.linear}
                }
            })
            .addCase(setPerson.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    properties: {...state.draft.properties, person: action.payload}
                }
            })
            .addCase(setLines.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    properties: {...state.draft.properties, lines: action.payload}
                }
            })
            .addCase(updateSynopsis.fulfilled, (state, action) => {
                state.draft = {...state.draft, properties: {...state.draft.properties, synopsis: action.payload}}
            })
            .addCase(updateTitle.fulfilled, (state, action) => {
                state.draft = {...state.draft, story: {...state.draft.story, title: action.payload}}
            })
            .addCase(updateLinesContent.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    story: {
                        ...state.draft.story,
                        lines: action.payload
                    }
                }
            })
            .addCase(updateActs.fulfilled, (state, action) => {
                let chid = 1
                state.draft = {
                    ...state.draft,
                    story: {
                        ...state.draft.story, acts: state.draft.story.acts.map((act, index) => {
                            return {
                                ...act,
                                id: action.payload[index].id,
                                description: action.payload[index].description,
                                title: action.payload[index].title,
                                chapters: (action.payload[index].chapters ?? []).map((ch: any) => {
                                    return {...ch, id: chid++}
                                }) ?? []
                            }
                        })
                    }
                }
            })
            .addCase(updateLine.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    story: {
                        ...state.draft.story, lines: state.draft.story.lines.map((line) => {
                            if (line.id == action.payload.id) {
                                return {
                                    ...line,
                                    title: action.payload.title,
                                    description: action.payload.description,
                                }
                            }
                            return {
                                ...line
                            }
                        })
                    }
                }
            })
            .addCase(updateAct.fulfilled, (state, action) => {
                let chid = 1
                state.draft = {
                    ...state.draft,
                    story: {
                        ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                            if (act.id == action.payload.id) {
                                return {
                                    ...act,
                                    title: action.payload.title,
                                    description: action.payload.description,
                                    chapters: (action.payload.chapters ?? []).map((ch: any) => {
                                        return {...ch, id: chid++}
                                    }) ?? []
                                }
                            }
                            return {
                                ...act, chapters: (act.chapters ?? []).map((ch: any) => {
                                    return {...ch, id: chid++}
                                }) ?? []
                            }
                        })
                    }
                }
            })
            .addCase(updateChapter.fulfilled, (state, action) => {
                    let scid = 1
                    state.draft = {
                        ...state.draft,
                        story: {
                            ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                                return {
                                    ...act,
                                    chapters: (act.chapters ?? []).map((ch) => {
                                        if (ch.id == action.payload.id) {
                                            return {
                                                ...ch,
                                                title: action.payload.title,
                                                description: action.payload.description,
                                                sections: (action.payload.sections ?? []).map((sc: any) => {
                                                    return {...sc, id: scid++}
                                                }) ?? []
                                            }
                                        }
                                        return {
                                            ...ch, sections: (ch.sections ?? []).map((sc: any) => {
                                                return {...sc, id: scid++}
                                            }) ?? []
                                        }
                                    })
                                }
                            })
                        }
                    }
                }
            )
            .addCase(updateSection.fulfilled, (state, action) => {
                    let scid = 1
                    state.draft = {
                        ...state.draft,
                        story: {
                            ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                                return {
                                    ...act,
                                    chapters: (act.chapters ?? []).map((ch) => {
                                        return {
                                            ...ch,
                                            sections: (ch.sections ?? []).map((s) => {
                                                if (s.id == action.payload.id) {
                                                    return {
                                                        ...s,
                                                        title: action.payload.title,
                                                        description: action.payload.description,
                                                        scenes: (action.payload.scenes ?? []).map((sc: any) => {
                                                            return {...sc, id: scid++}
                                                        }) ?? []
                                                    }
                                                }
                                                return {
                                                    ...s, scenes: (s.scenes ?? []).map((sc: any) => {
                                                        return {...sc, id: scid++}
                                                    }) ?? []
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                }
            )
            .addCase(updateScene.fulfilled, (state, action) => {
                    let shid = 1
                    state.draft = {
                        ...state.draft,
                        story: {
                            ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                                return {
                                    ...act,
                                    chapters: (act.chapters ?? []).map((ch) => {
                                        return {
                                            ...ch,
                                            sections: (ch.sections ?? []).map((s) => {
                                                return {
                                                    ...s,
                                                    scenes: (s.scenes ?? []).map((sc) => {
                                                        if (sc.id == action.payload.id) {
                                                            return {
                                                                ...sc,
                                                                title: action.payload.title,
                                                                description: action.payload.description,
                                                                shots: (action.payload.shots ?? []).map((sh: any) => {
                                                                    return {...sh, id: shid++}
                                                                }) ?? []
                                                            }
                                                        }
                                                        return {
                                                            ...sc, shots: (sc.shots ?? []).map((sh: any) => {
                                                                return {...sh, id: shid++}
                                                            }) ?? []
                                                        }
                                                    })
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                }
            )
            .addCase(updateShot.fulfilled, (state, action) => {
                    state.draft = {
                        ...state.draft,
                        story: {
                            ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                                return {
                                    ...act,
                                    chapters: (act.chapters ?? []).map((ch) => {
                                        return {
                                            ...ch,
                                            sections: (ch.sections ?? []).map((s) => {
                                                return {
                                                    ...s,
                                                    scenes: (s.scenes ?? []).map((sc) => {
                                                        return {
                                                            ...sc,
                                                            shots: (sc.shots ?? []).map((sh: any) => {
                                                                if (sh.id == action.payload.id) {
                                                                    return {
                                                                        ...sh,
                                                                        title: action.payload.title,
                                                                        description: action.payload.description,
                                                                        place: action.payload.place,
                                                                        time: action.payload.time,
                                                                        environment: action.payload.environment,
                                                                        narration: {
                                                                            text: action.payload.narration.text,
                                                                            character: action.payload.narration.character,
                                                                            expression: action.payload.narration.expression
                                                                        }
                                                                    }
                                                                }
                                                                return sh
                                                            }) ?? []
                                                        }
                                                    })
                                                }
                                            })
                                        }
                                    })
                                }
                            })
                        }
                    }
                }
            )
            .addCase(addChapter.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    story: {
                        ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                            if (act.id == action.payload.actId) {
                                return {
                                    ...act,
                                    // @ts-ignore
                                    chapters: (act.chapters ?? []).concat([action.payload.chapter]).sort((a, b) => a.id - b.id)
                                }
                            }
                            return act
                        })
                    }
                }
            })
            .addCase(addScene.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    story: {
                        ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                            return {
                                ...act,
                                chapters: (act.chapters ?? []).map((ch) => {
                                    if (ch.id == action.payload.chapterId) {
                                        return {
                                            ...ch,
                                            // @ts-ignore
                                            scenes: (ch.scenes ?? []).concat([action.payload.scene]).sort((a, b) => a.id - b.id)
                                        }
                                    }
                                    return ch
                                })
                            }
                        })
                    }
                }
            })
            .addCase(addShot.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    story: {
                        ...state.draft.story, acts: state.draft.story.acts.map((act) => {
                            return {
                                ...act,
                                chapters: (act.chapters ?? []).map((ch) => {
                                    return {
                                        ...ch,
                                        sections: (ch.sections ?? []).map((s) => {
                                            return {
                                                ...s,
                                                scenes: (s.scenes ?? []).map((sc) => {
                                                    if (sc.id == action.payload.sceneId) {
                                                        return {
                                                            ...sc,
                                                            // @ts-ignore
                                                            shots: (sc.shots ?? []).concat([action.payload.shot]).sort((a, b) => a.id - b.id)
                                                        }
                                                    }
                                                    return sc
                                                })
                                            }
                                        })
                                    }
                                })
                            }
                        })
                    }
                }
            })
            .addCase(updateSetting.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        title: action.payload.setting.title,
                        description: action.payload.setting.description,
                    }
                }
            })
            .addCase(addSettingCategory.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: (state.draft.setting.categories ?? []).concat([action.payload.category])
                    }
                }
            })
            .addCase(deleteSettingCategory.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.filter((c) => c.category != action.payload.categoryKey)
                    }
                }
            })
            .addCase(updateSettingCategory.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.map((c) => {
                            if (c.category == action.payload.category.category) {
                                return {
                                    ...c,
                                    title: action.payload.category.title,
                                    description: action.payload.category.description,
                                    components: action.payload.category.components
                                }
                            }
                            return {...c}
                        })
                    }
                }
            })
            .addCase(updateComponent.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.map((c) => {
                            if (c.category == action.payload.category) {
                                return {
                                    ...c,
                                    components: c.components.map((e) => {
                                        if (e.id == action.payload.component.id) {
                                            return {
                                                ...e,
                                                title: action.payload.component.title,
                                                description: action.payload.component.description,
                                                elements: action.payload.component.elements
                                            }
                                        }
                                        return {...e}
                                    })
                                }
                            }
                            return c
                        })
                    }
                }
            })
            .addCase(addComponents.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.map((c) => {
                            if (c.category == action.payload.category) {
                                return {
                                    ...c,
                                    components: c.components.concat(action.payload.components)
                                }
                            }
                            return c
                        })
                    }
                }
            })
            .addCase(deleteComponent.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.map((c) => {
                            if (c.category == action.payload.category) {
                                return {
                                    ...c,
                                    components: c.components.filter((e) => e.id != action.payload.componentId)
                                }
                            }
                            return c
                        })
                    }
                }
            })
            .addCase(updateElements.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.map((c) => {
                            if (c.category == action.payload.category) {
                                return {
                                    ...c,
                                    components: c.components.map((e) => {
                                        if (e.id == action.payload.componentId) {
                                            return {
                                                ...e,
                                                elements: e.elements.map((el) => {
                                                    const element = action.payload.elements.find(e => e.id == el.id)
                                                    if (element) {
                                                        return {
                                                            ...el,
                                                            title: element.title,
                                                            description: element.description,
                                                            embedded: element.embedded,
                                                            items: element.items ?? []
                                                        }
                                                    }
                                                    return el
                                                })
                                            }
                                        }
                                        return e
                                    })
                                }
                            }
                            return c
                        })
                    }
                }
            })
            .addCase(addElements.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.map((c) => {
                            if (c.category == action.payload.category) {
                                return {
                                    ...c,
                                    components: c.components.map((e) => {
                                        if (e.id == action.payload.componentId) {
                                            return {
                                                ...e,
                                                elements: e.elements.concat(action.payload.elements)
                                            }
                                        }
                                        return e
                                    })
                                }
                            }
                            return c
                        })
                    }
                }
            })

            .addCase(deleteElement.fulfilled, (state, action) => {
                state.draft = {
                    ...state.draft,
                    setting: {
                        ...state.draft.setting,
                        categories: state.draft.setting.categories.map((c) => {
                            if (c.category == action.payload.category) {
                                return {
                                    ...c,
                                    components: c.components.map((e) => {
                                        if (e.id == action.payload.componentId) {
                                            return {
                                                ...e,
                                                elements: e.elements.filter((el) => el.id != action.payload.elementId)
                                            }
                                        }
                                        return e
                                    })
                                }
                            }
                            return c
                        })
                    }
                }
            })
            .addCase(addLoreItem.fulfilled, (state, action) => {
                    state.draft = {
                        ...state.draft,
                        setting: {
                            ...state.draft.setting,
                            categories: state.draft.setting.categories.map((c) => {
                                if (c.category == action.payload.category) {
                                    return {
                                        ...c,
                                        components: c.components.map((e) => {
                                            if (e.id == action.payload.componentId) {
                                                return {
                                                    ...e,
                                                    elements: e.elements.map((el) => {
                                                        if (el.id == action.payload.elementId) {
                                                            return {
                                                                ...el,
                                                                items: el.items?.concat(action.payload.item) ?? [action.payload.item]
                                                            }
                                                        }
                                                        return el
                                                    })
                                                }
                                            }
                                            return e
                                        })
                                    }
                                }
                                return c
                            })
                        }
                    }
                }
            ).addCase(updateLoreItems.fulfilled, (state, action) => {
            state.draft = {
                ...state.draft,
                setting: {
                    ...state.draft.setting,
                    categories: state.draft.setting.categories.map((c) => {
                        if (c.category == action.payload.category) {
                            return {
                                ...c,
                                components: c.components.map((e) => {
                                    if (e.id == action.payload.componentId) {
                                        return {
                                            ...e,
                                            elements: e.elements.map((el) => {
                                                if (el.id == action.payload.elementId) {
                                                    return {
                                                        ...el,
                                                        items: el.items.map((it) => {
                                                            const item = action.payload.items.find(e => e.id == it.id)
                                                            if (item) {
                                                                return {
                                                                    ...it,
                                                                    title: item.title,
                                                                    description: item.description,
                                                                    embedded: item.embedded
                                                                }
                                                            }
                                                            return it
                                                        })
                                                    }
                                                }
                                                return el
                                            })
                                        }
                                    }
                                    return e
                                })
                            }
                        }
                        return c
                    })
                }
            }
        }).addCase(deleteLoreItem.fulfilled, (state, action) => {
            state.draft = {
                ...state.draft,
                setting: {
                    ...state.draft.setting,
                    categories: state.draft.setting.categories.map((c) => {
                        if (c.category == action.payload.category) {
                            return {
                                ...c,
                                components: c.components.map((e) => {
                                    if (e.id == action.payload.componentId) {
                                        return {
                                            ...e,
                                            elements: e.elements.map((el) => {
                                                if (el.id == action.payload.elementId) {
                                                    return {
                                                        ...el,
                                                        items: el.items.filter((it) => it.id != action.payload.itemId)
                                                    }
                                                }
                                                return el
                                            })
                                        }
                                    }
                                    return e
                                })
                            }
                        }
                        return c
                    })
                }
            }
        })


    }
})


export default draftSlice.reducer;